/**
 * What a time-and-materials service sells for, and what the customer sees of it.
 *
 * A T&M service is quoted as one price built from parts the customer never sees
 * individually: the labor, the things it consumes, the markup on those things,
 * and any surcharge. They exist to work out what the service costs — the
 * customer is buying the service, not a shopping list.
 *
 * Three amounts, never all called "price":
 *
 *   cost                 what we pay. Always counts toward job costing,
 *                        whatever the customer is shown.
 *   sellingContribution  what the part adds to its PARENT SERVICE's calculated
 *                        price: cost × quantity × (1 + markup).
 *   linePrice            what the part charges the customer on its own. Zero
 *                        unless it is sold separately.
 *
 * Markup and chargeability are INDEPENDENT. A part can lift the service's price
 * by its full margin and still never appear as a charge of its own.
 */

import {
  costContribution,
  pricingTreatmentOf,
  rendersOnCustomerDocument,
  type PricingTreatment,
} from './line-economics';

/**
 * The seven-field model — chargeability, visibility and the two contribution
 * rules — lives in `line-economics`, which is the ONE place that answers what a
 * line is worth. Re-exported here so the existing callers of this module keep
 * their import, and so there is still only one implementation behind it.
 */
export { isChargeable, pricingTreatmentOf, type PricingTreatment } from './line-economics';

export type SurchargeType = 'FLAT' | 'PERCENT';

export interface ServiceSurcharge {
  type: SurchargeType;
  value: number;
  /**
   * What a percentage surcharge is charged on.
   *
   * A permit is a charge on the whole job; a parts-handling fee is a charge on
   * the parts only. Applying either to the wrong base changes what the customer
   * pays, so the base is chosen rather than assumed. Omitted means all three —
   * the sensible default, and what a flat surcharge ignores entirely.
   */
  appliesTo?: SurchargeBase;
}

export interface SurchargeBase {
  labor?: boolean;
  materials?: boolean;
  equipment?: boolean;
}

export interface ServicePart {
  /** MATERIAL or EQUIPMENT — decides which surcharge base it falls under. */
  type?: string;
  /** What we pay per unit. */
  costBasis?: number;
  /** What it sells for per unit — cost plus its own markup. */
  basePrice?: number;
  quantity?: number;
  /** An optional part the customer declined is not part of the price. */
  isSelected?: boolean;
  /**
   * Whether this part is billed at its own price. Absent means yes — every part
   * quoted before the flag existed was billed, and reading a missing value as
   * "not billed" would silently stop charging for it.
   *
   * The LINE's own answer, and only the default: what actually decides is
   * `pricingTreatment` on the relationship it came in through.
   */
  chargeable?: boolean | null;
  /** How this service charges for this part. Absent derives from `chargeable`. */
  pricingTreatment?: PricingTreatment | null;
  /**
   * Markup percentage this relationship uses instead of the part's own.
   *
   * Percent, like every other markup in the catalog — 20 means 20%. Absent
   * falls through to `markupPercent`, the part's own.
   */
  markupOverride?: number | null;
  /**
   * The part's OWN markup over cost, from the catalog.
   *
   * Read rather than assumed baked into `basePrice`. A catalog price is typed
   * independently of cost, so `basePrice` is not reliably `cost x (1 + markup)`
   * — and treating it as though it were is what made the same material worth
   * two different amounts depending on which screen added it.
   */
  markupPercent?: number | null;
}

export interface ServiceRollup {
  labor: number;
  partsCost: number;
  /** What the markup on the parts adds, over and above what they cost us. */
  partsMarkup: number;
  /**
   * partsCost + partsMarkup — what the parts contribute to the SERVICE's price.
   *
   * Every part contributes here, chargeable or not: markup and chargeability
   * are independent, and a part consumed by the service still lifts what the
   * service is worth.
   */
  partsSellingContribution: number;
  /**
   * Of that contribution, the amount belonging to parts that bill the customer
   * on their OWN line.
   *
   * Whoever materializes this service onto a document has to take this off the
   * service line, or the customer is charged for those parts twice — once
   * inside the service, once on their own row. See the double-charging rule.
   */
  partsSeparatelyCharged: number;
  /** Labor and parts, before any surcharge. */
  preSurcharge: number;
  surcharge: number;
  /** What the surcharge was charged on, so the figure can be explained. */
  surchargeBase: number;
  total: number;
}

const num = (value: number | null | undefined): number =>
  Number.isFinite(value) ? (value as number) : 0;

const round = (value: number): number => Math.round(value * 100) / 100;

/**
 * A SERVICE sold for one price the operator named, rather than one summed from
 * what it consumes.
 *
 * The distinction decides what the parts are FOR. Time & materials builds the
 * price out of the labor and the parts, so each part is money the customer owes.
 * A flat price states what the outcome costs to buy, and the parts are what
 * delivering it costs US — a $2,000 flat service carrying $1,555 of parts sells
 * for $2,000, not $3,555, and its margin is 22.25% rather than 100%.
 *
 * Anything that is not a service answers false: only a service carries parts.
 * Among services, only time-and-materials sums, so every other strategy — flat,
 * labor-only, cost-plus — prices the service on its own terms and reads as flat
 * here. This is the same rule `isFlatPricePricebookService` states over the
 * pricebook's shape, and it delegates here so the catalog and the document
 * cannot answer differently.
 */
export function isFlatPricedService(line: {
  type?: string | null;
  priceStrategy?: string | null;
}): boolean {
  if (String(line.type ?? '').toUpperCase() !== 'SERVICE') return false;
  return String(line.priceStrategy ?? '').toUpperCase() !== 'DYNAMIC';
}

/**
 * The treatment to RECORD on a part line as it is CREATED under a service.
 *
 * Three answers, in order:
 *
 *   an explicit treatment  kept as-is. The relationship is the authority; the
 *                          strategy only supplies a default, exactly as the
 *                          catalog item's `chargeable` does.
 *   ANY service parent     `included_in_service`. The service's price already
 *                          covers the part either way — a flat price because
 *                          the operator typed one that does, a dynamic price
 *                          because the formula summed the part's marked-up
 *                          value INTO it. Billing the part again on its own row
 *                          is the double-charging rule broken, and on the
 *                          dynamic side it charged the markup twice over.
 *   anything else          null: a BUNDLE's members are its own priced lines,
 *                          so nothing is decided here and the catalog's
 *                          `chargeable` stays the default.
 *
 * Deliberately a CREATION-TIME derivation, written onto the line and saved with
 * it, rather than something `pricingTreatmentOf` infers on every read. A read-
 * time rule would reach back into documents already sent and change what they
 * charge — every saved estimate carrying a flat service and its parts would
 * silently drop to the service's price alone. What is already quoted keeps
 * whatever it was quoted at; only lines created from here on derive.
 */
export function partPricingTreatmentForService(
  part: { pricingTreatment?: PricingTreatment | null },
  service: { type?: string | null; priceStrategy?: string | null },
): PricingTreatment | null {
  if (part.pricingTreatment === 'included_in_service') return 'included_in_service';
  if (part.pricingTreatment === 'separate_charge') return 'separate_charge';
  // Every SERVICE, on either pricing mode. `isFlatPricedService` is NOT asked:
  // it answers how the price was ARRIVED at, and that is a different question
  // from whether the price already covers the parts. Both modes cover them.
  return String(service.type ?? '').toUpperCase() === 'SERVICE' ? 'included_in_service' : null;
}

/**
 * Which markup percentage applies, in the order the business states it.
 *
 * `markupOverride` (this relationship) → the item's own markup → the dynamic
 * pricing default for the item's family. Each rung is used only when the one
 * above it is absent; a rung set to 0 is an answer, not a gap, so it stops the
 * search rather than falling through to a higher percentage nobody chose.
 *
 * The item's own markup means nothing without a price to derive it from — see
 * `itemMarkupRung`.
 */
export function resolveMarkupPercent(rungs: {
  markupOverride?: number | null;
  itemMarkupPercent?: number | null;
  /** The item's catalog price, which is what makes `itemMarkupPercent` mean something. */
  itemBasePrice?: number | null;
  familyDefaultPercent?: number | null;
}): number {
  for (const rung of [
    rungs.markupOverride,
    itemMarkupRung(rungs.itemMarkupPercent, rungs.itemBasePrice),
    rungs.familyDefaultPercent,
  ]) {
    if (rung != null && Number.isFinite(rung)) return rung;
  }
  return 0;
}

/**
 * The item's OWN markup as a rung, which means nothing without a price.
 *
 * This rung is the only DERIVED one: `markupFromCostPrice` computes it from the
 * catalog price against the cost, clamped at -100. So it is a statement about
 * markup only where there is a price for it to be a statement about. On an item
 * whose Price was never filled in it is the shape of a blank field, and reading
 * it as a markup prices the part at `cost x (1 + -100/100)` = ZERO — the $199
 * material that contributed nothing to the $200 dynamic service consuming it,
 * at -199% margin (BR-77).
 *
 * With no price behind it the rung says only "no markup stated", which is 0% —
 * sell at cost. A price that EXISTS is honoured whatever its sign: an item
 * listed at 80 against a cost of 100 derives -20%, and that is a loss-leader
 * the operator priced, not an artifact. Keying on the price rather than on the
 * percentage matters because -100 is not a sentinel — it is the floor of a real
 * division, and a $199 part listed at $0.01 derives -99.99%.
 *
 * An ABSENT price is unknown, not zero, and leaves the markup as stated — only
 * a price we were actually told about, and that is not positive, collapses the
 * rung. Operations has no such case: its `basePrice` is a required column.
 *
 * Lockstep with `resolveMarkupPercent` in operations' `pricing-rules.ts`, which
 * is what prices the same rows in the catalog — a rule living on only one side
 * shows up as an estimate that disagrees with the pricebook it came from.
 */
function itemMarkupRung(
  itemMarkupPercent?: number | null,
  itemBasePrice?: number | null,
): number | null | undefined {
  if (itemMarkupPercent == null || !Number.isFinite(itemMarkupPercent)) return itemMarkupPercent;
  if (itemBasePrice == null || !Number.isFinite(itemBasePrice)) return itemMarkupPercent;
  return itemBasePrice > 0 ? itemMarkupPercent : 0;
}

/** The three amounts a part is worth — see the header. */
export interface PartAmounts {
  cost: number;
  /** What the markup adds over cost. */
  markup: number;
  /** cost + markup — what the part adds to its parent service's price. */
  sellingContribution: number;
  /** What the part charges the customer on its own row. */
  linePrice: number;
}

export function partAmounts(part: ServicePart): PartAmounts {
  // Offered, not sold: a declined optional part contributes nothing.
  if (part.isSelected === false)
    return { cost: 0, markup: 0, sellingContribution: 0, linePrice: 0 };

  const quantity = num(part.quantity);
  // RULE TWO, from the one module that owns it: cost × quantity, always.
  const cost = costContribution(part);

  // WHAT A PART IS WORTH TO ITS SERVICE: cost x (1 + markup).
  //
  // This is the `(Material Cost x Markup)` term of the dynamic formula, and it
  // is measured from COST. It used to fall back to `basePrice x quantity` — the
  // catalog's typed selling price — whenever no relationship override was set,
  // which was almost always. That is a different number: a catalog price is
  // typed by hand and is not reliably `cost x (1 + markup)`.
  //
  // The consequence was two answers for one part. The pricebook resolves a
  // component through `componentSellingUnitPrice`, which prices COST_PLUS parts
  // at cost x markup; the document resolved the same part at its catalog price.
  // So the same material, on the same service, was worth one amount when it
  // came from the pricebook's parts list and another when it was added on the
  // estimate.
  //
  // Cost x markup is used only where a markup is actually STATED — the
  // relationship's, then the part's own. Silence is not 0%: a part that states
  // no markup has `basePrice` as the only account of what it sells for, and
  // reading silence as zero would mark every such part down to cost.
  //
  // A part with no recorded COST keeps its catalog price for the same reason —
  // there is nothing to mark up, and pricing it at zero would give it away.
  // This is the reading the pricebook already takes (`componentSellingUnitPrice`).
  // The item's own markup is read through the same rung as `resolveMarkupPercent`
  // above: it is DERIVED from the catalog price, so on a part costing money at a
  // price nobody typed it states -100% and would give the part away. Without a
  // price it says only "no markup stated" — 0%, sell at cost. The relationship's
  // override keeps its sign either way; that one somebody chose.
  const statedMarkup = [
    part.markupOverride,
    itemMarkupRung(part.markupPercent, part.basePrice),
  ].find((rung) => rung != null && Number.isFinite(rung));
  const sellingContribution =
    statedMarkup != null && cost > 0
      ? cost * (1 + statedMarkup / 100)
      : num(part.basePrice) * quantity;

  // THE RULE: a part contributes its full cost × qty × (1 + markup) to the
  // service's price whether or not the customer is charged for it separately.
  // Markup and chargeability are independent questions — the markup is what the
  // shop earns for sourcing, carrying and warrantying the part, and it earns
  // that whether the part is named on the invoice or buried in the service.
  // What chargeability decides is only `linePrice`: whether there is a SECOND,
  // separate charge for the same part. An included part has none, because its
  // money is already inside the service.
  //
  // (This replaces the old behaviour, which zeroed a non-chargeable part's
  // markup and so quietly sold every consumed part at cost.)
  //
  // Markup is reported as the signed difference: a part priced below cost has
  // negative markup, which is a real (bad) number and belongs in front of
  // somebody rather than clamped away.
  return {
    cost,
    markup: sellingContribution - cost,
    sellingContribution,
    linePrice: pricingTreatmentOf(part) === 'separate_charge' ? sellingContribution : 0,
  };
}

/**
 * Roll a service's labor, parts and surcharge into the one price it sells for.
 *
 * A percentage surcharge is charged on whichever of labor, materials and
 * equipment it was pointed at — a permit is a charge on the whole job, a
 * parts-handling fee only on the parts. It never compounds onto complexity or
 * after-hours, which are adjustments in their own right.
 */
export function rollUpService(args: {
  labor: number;
  parts: ServicePart[];
  surcharge?: ServiceSurcharge | null;
  /** Fixed adjustments already resolved elsewhere (complexity, after-hours). */
  otherModifiers?: number;
}): ServiceRollup {
  const labor = num(args.labor);

  let partsCost = 0;
  let partsMarkup = 0;
  let partsSeparatelyCharged = 0;
  let materialsTotal = 0;
  let equipmentTotal = 0;
  for (const part of args.parts) {
    const amounts = partAmounts(part);
    partsCost += amounts.cost;
    partsMarkup += amounts.markup;
    partsSeparatelyCharged += amounts.linePrice;
    // The surcharge base is what the parts are WORTH to this service, which is
    // their contribution — a parts-handling fee is earned on handling the part,
    // not on whether it ends up itemised on the customer's copy.
    if (String(part.type ?? '').toUpperCase() === 'EQUIPMENT')
      equipmentTotal += amounts.sellingContribution;
    else materialsTotal += amounts.sellingContribution;
  }

  const preSurcharge = labor + partsCost + partsMarkup + num(args.otherModifiers);

  // A percentage surcharge is charged only on the parts of the job it was
  // chosen to apply to; a flat one ignores the base entirely.
  const applies = args.surcharge?.appliesTo;
  const wants = (key: keyof SurchargeBase) => (applies ? applies[key] === true : true);
  const surchargeBase =
    (wants('labor') ? labor : 0) +
    (wants('materials') ? materialsTotal : 0) +
    (wants('equipment') ? equipmentTotal : 0);

  const surcharge = args.surcharge
    ? args.surcharge.type === 'FLAT'
      ? num(args.surcharge.value)
      : surchargeBase * (num(args.surcharge.value) / 100)
    : 0;

  return {
    labor: round(labor),
    partsCost: round(partsCost),
    partsMarkup: round(partsMarkup),
    partsSellingContribution: round(partsCost + partsMarkup),
    partsSeparatelyCharged: round(partsSeparatelyCharged),
    preSurcharge: round(preSurcharge),
    surcharge: round(surcharge),
    surchargeBase: round(surchargeBase),
    total: round(preSurcharge + surcharge),
  };
}

/**
 * Whether a line is shown to the customer on the document.
 *
 * The rule itself lives in `line-economics` alongside chargeability, because
 * the two are constantly mistaken for each other and keeping them in one file
 * is what makes the difference readable: visibility decides whether the row
 * prints and NOTHING about the money. Kept exported here under its old name so
 * existing callers are unaffected.
 */
export function isCustomerVisible(line: {
  type?: string | null;
  parentLineKey?: string | null;
  chargeable?: boolean | null;
  pricingTreatment?: PricingTreatment | null;
  unitPrice?: number | null;
  hiddenFromCustomer?: boolean | null;
  visibleToCustomer?: boolean | null;
}): boolean {
  return rendersOnCustomerDocument(line);
}
